import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { useVerifyOtpMutation } from "@/lib/services/data-layer/core";
import { OtpAction } from "@/pages/auth/otp/otp-reducer";
import { FC } from "react";
import TimerAndResendOtp from "./timer-and-resend-otp.component";
import OtpInput from "react-otp-input";
import { Input } from "@/components/ui/input";
import { useAuthStore } from "@/lib/store";

const otpSchema = z.object({
  otp: z
    .string()
    .length(6, "کد تأیید باید 6 کاراکتر باشد")
    .regex(/^\d+$/, "کد تأیید باید فقط شامل اعداد باشد"),
});

export interface OtpFormValues {
  otp: string;
}

type VerifyOtpFormProps = {
  dispatch: React.Dispatch<OtpAction>;
  phoneNumber?: string;
  timer: number;
};

const VerifyOtpForm: FC<VerifyOtpFormProps> = ({
  phoneNumber,
  timer,
  dispatch,
}) => {
  const { verifyOtp } = useAuthStore();

  const form = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      otp: "",
    },
    reValidateMode: "onChange",
    mode: "onChange",
  });

  const { mutate, isPending } = useVerifyOtpMutation();

  const verifyOtpHandler = ({
    otp,
    phoneNumber,
  }: {
    otp: string;
    phoneNumber: string;
  }) => {
    mutate(
      { mobile: phoneNumber, otp },
      {
        onSuccess: (data) => {
          if (!phoneNumber) return;
          verifyOtp({ mobile: phoneNumber, otp, account: data });
        },
        onError: (error) => {
          form.setError("otp", {
            type: "manual",
            message: error?.message,
          });
          form.setFocus("otp");
        },
      }
    );
  };

  const onOtpSubmit = (data: OtpFormValues) => {
    if (!phoneNumber) return;
    verifyOtpHandler({
      phoneNumber,
      otp: data.otp,
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onOtpSubmit)}>
        <div className="w-fit mx-auto mb-[43px]">
          <div className="flex flex-row gap-2 text-center mb-[8px] text-gray-500 ">
            <span>لطفا کد پیامک شده به شماره</span>
            <span className="font-bold text-gray-600">{phoneNumber}</span>
            <span> را وارد نمایید.</span>
             
          </div>
          <TimerAndResendOtp
            timer={timer}
            phoneNumber={phoneNumber}
            dispatch={dispatch}
            resetForm={form.reset}
          />
          <FormField
            control={form.control}
            name="otp"
            render={({ field }) => (
              <FormItem dir="ltr">
                <FormControl>
                  <OtpInput
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      if (value.length === 6) {
                        form.setValue("otp", value);
                        form.trigger("otp").then((isValid) => {
                          if (isValid) {
                            form.handleSubmit(onOtpSubmit)();
                          }
                        });
                      }
                    }}
                    numInputs={6}
                    renderInput={(props, i) => (
                      <Input
                        {...props}
                        className="!w-[40px] !h-[45px] sm:h-10 sm:w-10 text-[20px] p-0 rounded-lg border-[#CCCCCC] font-bold"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        onKeyPress={(e) => {
                          const isDigit = /^\d$/.test(e.key);
                          const isControlKey =
                            e.key === "Backspace" ||
                            e.key === "Delete" ||
                            e.key === "ArrowLeft" ||
                            e.key === "ArrowRight" ||
                            e.key === "Tab";

                          if (!isDigit && !isControlKey) {
                            e.preventDefault();
                          }
                        }}
                        onPaste={(e) => {
                          const pastedData = e.clipboardData.getData("text");
                          if (!/^\d+$/.test(pastedData)) {
                            e.preventDefault();
                          }
                        }}
                        autoFocus={i === 0}
                      />
                    )}
                    inputStyle={{
                      width: "100%",
                      height: "100%",
                    }}
                    containerStyle="flex gap-2 justify-between ltr"
                  />
                </FormControl>
                <FormMessage className="text-center" />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col items-center">
          <Button
            type="submit"
            className="w-full font-bold rounded-2xl"
            disabled={isPending}
          >
            {isPending ? "درحال بررسی..." : "ورود"}
          </Button>
          <Button
            variant="link"
            className="mt-[18px] text-sm font-bold text-primary p-0 h-fit"
            onClick={() => dispatch({ type: "RESET" })}
          >
            اصلاح شماره موبایل
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default VerifyOtpForm;
